import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const Projects = () => {
  const { projects } = useSelector(state => state.projects);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    document.title = "Projects | Musa Yusuf Magoban";
    window.scrollTo(0, 0);
  }, []);

  // Extract unique categories from projects
  const categories = ['all', ...new Set(projects.flatMap(p => p.tools.slice(0, 1)))];
  
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.tools.some(tool => tool.toLowerCase().includes(filter.toLowerCase())));

  const projectStats = [
    { number: projects.length, label: "Total Projects", icon: "FolderOpen", color: "primary" },
    { number: projects.filter(p => p.github).length, label: "Open Source", icon: "Github", color: "secondary" },
    { number: new Set(projects.flatMap(p => p.tools)).size, label: "Technologies", icon: "Code", color: "accent" },
    { number: "100%", label: "Hands-On", icon: "Zap", color: "primary" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Projects | Musa Yusuf Magoban</title>
        <meta name="description" content="Hands-on cybersecurity projects including SOC lab, malware analysis, SOAR automation, and AI-powered security tools." />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Enhanced Header Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary font-medium text-sm mb-6">
                <Icon name="Sparkles" size={16} className="mr-2" />
                Portfolio Showcase
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                My Work <span className="text-primary">At A Glance</span>
              </h1>
              <p className="text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
                Hands-on experience in building a <span className="text-primary font-medium">SOC home lab</span>, 
                integrating <span className="text-secondary font-medium">AI for threat detection</span>, 
                developing <span className="text-accent font-medium">ethical malware</span>, and automating 
                incident response with <span className="text-primary font-medium">SOAR tools</span>.
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
                {projectStats.map((stat, index) => (
                  <div key={index} className="bg-surface/80 backdrop-blur-sm rounded-xl p-4 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1">
                    <div className={`w-10 h-10 bg-${stat.color}/10 rounded-lg flex items-center justify-center mx-auto mb-2`}>
                      <Icon name={stat.icon} size={20} className={`text-${stat.color}`} />
                    </div>
                    <div className="text-2xl font-bold text-foreground">{stat.number}</div>
                    <div className="text-sm text-text-secondary">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-surface/50 border-b border-border/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span className="text-sm text-text-secondary font-medium mr-4">Filter by:</span>
              {categories.slice(0, 5).map((category) => (
                <button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    filter === category
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
                      : 'bg-surface text-text-secondary hover:bg-primary/10 hover:text-primary'
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Projects Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project) => (
                <div 
                  key={project.id} 
                  className="group bg-surface rounded-xl p-6 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-2 border border-border/50 hover:border-primary/20 relative overflow-hidden"
                  onMouseEnter={() => setHoveredProject(project.id)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon with enhanced styling */}
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon name={project.icon} size={28} className="text-primary group-hover:text-secondary transition-colors duration-300" />
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                      {project.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-text-secondary mb-6 leading-relaxed line-clamp-3">
                      {project.description}
                    </p>
                    
                    {/* Tools Section */}
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-foreground">Technologies</h4>
                        <span className="text-xs text-text-secondary">{project.tools.length} tools</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {project.tools.slice(0, 3).map((tool, toolIndex) => (
                          <span 
                            key={toolIndex}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                          >
                            {tool}
                          </span>
                        ))}
                        {project.tools.length > 3 && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-text-secondary/10 text-text-secondary">
                            +{project.tools.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* GitHub Link */}
                    {project.github && (
                      <div className="pt-4 border-t border-border/30">
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 w-full px-4 py-3 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-all duration-200 group/link"
                        >
                          <Icon name="Github" size={16} />
                          <span className="flex-1 text-left">View on GitHub</span>
                          <Icon name="ExternalLink" size={14} className="group-hover/link:translate-x-1 transition-transform" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* No results message */}
            {filteredProjects.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No projects found</h3>
                <p className="text-text-secondary">Try adjusting your filter or browse all projects.</p>
                <button
                  onClick={() => setFilter('all')}
                  className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Show All Projects
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Enhanced Project Impact Section */}
        <section className="py-20 bg-gradient-to-br from-muted/30 to-muted/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-secondary/10 rounded-full text-secondary font-medium text-sm mb-6">
                <Icon name="TrendingUp" size={16} className="mr-2" />
                Impact & Learning
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Project Impact
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Practical experience gained through hands-on cybersecurity projects, 
                demonstrating real-world application of security concepts.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Learning Outcomes */}
              <div className="bg-surface rounded-xl p-8 security-shadow">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                  <Icon name="Brain" size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Key Learning Outcomes</h3>
                <ul className="space-y-3">
                  {[
                    'Advanced threat detection and response',
                    'Security automation and orchestration',
                    'Malware analysis and reverse engineering',
                    'AI/ML integration in cybersecurity',
                    'Digital forensics and incident response'
                  ].map((outcome, index) => (
                    <li key={index} className="flex items-start">
                      <Icon name="CheckCircle" size={16} className="text-accent mr-3 mt-1 flex-shrink-0" />
                      <span className="text-text-secondary">{outcome}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technical Skills */}
              <div className="bg-surface rounded-xl p-8 security-shadow">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-6">
                  <Icon name="Code" size={24} className="text-secondary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-4">Technical Proficiency</h3>
                <div className="space-y-4">
                  {[
                    { skill: 'SOC Operations', level: '95%' },
                    { skill: 'Malware Analysis', level: '88%' },
                    { skill: 'Security Automation', level: '92%' },
                    { skill: 'Digital Forensics', level: '85%' }
                  ].map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-foreground font-medium">{item.skill}</span>
                        <span className="text-text-secondary">{item.level}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full transition-all duration-1000"
                          style={{ width: item.level }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 bg-gradient-to-br from-primary to-secondary relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  Ready to Collaborate?
                </h2>
                <p className="text-xl text-white/90 max-w-2xl mx-auto">
                  Let's discuss how my practical cybersecurity experience can contribute to your organization's security goals.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.location.href = '/contact'}
                  className="bg-white text-primary px-8 py-3 rounded-lg font-medium hover:bg-white/90 transition-all duration-200 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                >
                  <Icon name="Mail" size={18} className="mr-2 inline" />
                  Get In Touch
                </button>
                <button
                  onClick={() => window.location.href = '/certifications'}
                  className="border-2 border-white/30 text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200"
                >
                  <Icon name="Award" size={18} className="mr-2 inline" />
                  View Certifications
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Projects; 